import type { MfgRecipeKind, MfgRecipeSummary } from "./types";
import { visibleRecipeTags } from "./variantConfig.js";

export type RecipeMetaIntersection = {
  kind: MfgRecipeKind | "mixed";
  is_active: boolean | "mixed";
  recipe_group_id: string | null | "mixed";
  tagsCommon: string[];
  tagsUnion: string[];
  nameSample: string[];
};

function sortedUnique(values: Iterable<string>): string[] {
  return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
}

/** Fields identical across every selected recipe (visible tags only). */
export function computeRecipeMetaIntersection(
  recipes: readonly MfgRecipeSummary[],
): RecipeMetaIntersection {
  if (recipes.length === 0) {
    return {
      kind: "mixed",
      is_active: "mixed",
      recipe_group_id: "mixed",
      tagsCommon: [],
      tagsUnion: [],
      nameSample: [],
    };
  }

  const first = recipes[0]!;
  let kind: MfgRecipeKind | "mixed" = first.kind;
  let is_active: boolean | "mixed" = first.is_active;
  let recipe_group_id: string | null | "mixed" = first.recipe_group_id ?? null;

  for (let i = 1; i < recipes.length; i++) {
    const r = recipes[i]!;
    if (kind !== "mixed" && r.kind !== kind) kind = "mixed";
    if (is_active !== "mixed" && r.is_active !== is_active) is_active = "mixed";
    if (recipe_group_id !== "mixed" && (r.recipe_group_id ?? null) !== recipe_group_id) {
      recipe_group_id = "mixed";
    }
  }

  const visibleSets = recipes.map(
    (r) => new Set(visibleRecipeTags(r.tags ?? []).map((t) => t.toLowerCase())),
  );
  const firstVisible = visibleRecipeTags(first.tags ?? []);
  const tagsCommon = firstVisible.filter((t) =>
    visibleSets.every((set) => set.has(t.toLowerCase())),
  );

  const unionRaw: string[] = [];
  const unionSeen = new Set<string>();
  for (const r of recipes) {
    for (const t of visibleRecipeTags(r.tags ?? [])) {
      const key = t.toLowerCase();
      if (unionSeen.has(key)) continue;
      unionSeen.add(key);
      unionRaw.push(t);
    }
  }

  return {
    kind,
    is_active,
    recipe_group_id,
    tagsCommon: sortedUnique(tagsCommon),
    tagsUnion: sortedUnique(unionRaw),
    nameSample: recipes.slice(0, 5).map((r) => `${r.code} · ${r.name}`),
  };
}
